'use client';

/**
 * Notification Item Component
 * 
 * Displays individual notification with actions (mark as read, dismiss).
 */

import React from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/navigation';
import { X, Check, AlertCircle, Info, AlertTriangle, Bell } from 'lucide-react';
import { AppDispatch } from '@/app/store';
import { markAsRead, dismissNotification } from '@/app/store/thunks/notificationThunks';
import { Notification } from '../types/notification.types';
import { formatDistanceToNow } from 'date-fns';

interface NotificationItemProps {
  notification: Notification;
}

export const NotificationItem = React.memo<NotificationItemProps>(({ notification }) => {
  const dispatch = useDispatch<AppDispatch>();
  const router = useRouter();

  const handleMarkAsRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!notification.isRead) {
      await dispatch(markAsRead(notification.id));
    }
  };

  const handleDismiss = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await dispatch(dismissNotification(notification.id));
  };

  const handleClick = () => {
    // Mark as read when clicked
    if (!notification.isRead) {
      dispatch(markAsRead(notification.id));
    }

    // Navigate to action URL if provided - let page-level auth handle redirects
    if (notification.actionUrl) {
      router.push(notification.actionUrl);
    }
  };

  // Get priority icon and color
  const getPriorityIcon = () => {
    switch (notification.notificationPriority) {
      case 'urgent':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'high':
        return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      case 'medium':
        return <Info className="w-5 h-5 text-blue-500" />;
      case 'low':
      default:
        return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  // Get priority background color
  const getPriorityBgColor = () => {
    if (notification.isRead) {
      return 'bg-gray-50 dark:bg-gray-800/50';
    }
    switch (notification.notificationPriority) {
      case 'urgent':
        return 'bg-red-50 dark:bg-red-900/10';
      case 'high':
        return 'bg-orange-50 dark:bg-orange-900/10';
      case 'medium':
        return 'bg-blue-50 dark:bg-blue-900/10';
      case 'low':
      default:
        return 'bg-white dark:bg-gray-800';
    }
  };

  // Format timestamp
  const getTimeAgo = () => {
    try {
      return formatDistanceToNow(new Date(notification.sentAt), { addSuffix: true });
    } catch {
      return 'Recently';
    }
  };

  return (
    <div
      className={`relative p-4 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors cursor-pointer ${getPriorityBgColor()}`}
      onClick={handleClick}
    >
      {/* Unread Indicator */}
      {!notification.isRead && (
        <div className="absolute left-2 top-1/2 transform -translate-y-1/2 w-2 h-2 bg-blue-600 rounded-full"></div>
      )}

      <div className="flex gap-3 ml-4">
        {/* Priority Icon */}
        <div className="flex-shrink-0 mt-1">
          {getPriorityIcon()}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <h4 className={`text-sm font-semibold ${notification.isRead ? 'text-gray-600 dark:text-gray-400' : 'text-gray-900 dark:text-white'}`}>
            {notification.notificationTitle}
          </h4>

          {/* Message */}
          <p className={`text-sm mt-1 ${notification.isRead ? 'text-gray-500 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>
            {notification.notificationMessage}
          </p>

          {/* Metadata */}
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 dark:text-gray-400">
            <span>{getTimeAgo()}</span>
            {notification.notificationCategory && (
              <>
                <span>•</span>
                <span className="capitalize">{notification.notificationCategory}</span>
              </>
            )}
            {notification.senderUserName && (
              <>
                <span>•</span>
                <span>From: {notification.senderUserName}</span>
              </>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex-shrink-0 flex items-start gap-1">
          {!notification.isRead && (
            <button
              onClick={handleMarkAsRead}
              className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded transition-colors"
              title="Mark as read"
              aria-label="Mark as read"
            >
              <Check className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={handleDismiss}
            className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded transition-colors"
            title="Dismiss"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
});
